import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminSidebar from '../../components/admin/AdminSidebar'
import './AdminDashboard.css'
import { apiFetch } from '../../utils/api'

//const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const INCOME_CATEGORIES = ['project', 'consultation', 'retainer', 'other']

const CAT_COLORS = {
  project:      '#FFB7CE',
  consultation: '#64b4ff',
  retainer:     '#78dca0',
  other:        'rgba(255,255,255,0.3)',
}

function fmt(n) {
  return `KSh ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 0 })}`
}

function shortMonth(ym) {
  if (!ym) return ''
  const [y, m] = ym.split('-')
  return new Date(y, m - 1).toLocaleString('en', { month: 'short' })
}

// ── Mini bar chart (CSS only) ─────────────────────────────────────
function BarChart({ data, valueKey, labelKey, color = '#FFB7CE', prefix = '' }) {
  if (!data?.length) return <p className='adash-empty-chart'>No data yet</p>
  const max = Math.max(...data.map(d => Number(d[valueKey]) || 0))
  return (
    <div className='adash-bar-chart'>
      {data.map((d, i) => {
        const pct = max ? ((Number(d[valueKey]) || 0) / max) * 100 : 0
        return (
          <div key={i} className='adash-bar-col'>
            <span className='adash-bar-value'>
              {prefix}{Number(d[valueKey] || 0).toLocaleString()}
            </span>
            <div className='adash-bar-track'>
              <div
                className='adash-bar-fill'
                style={{ height: `${pct}%`, background: color, animationDelay: `${i * 0.06}s` }}
              />
            </div>
            <span className='adash-bar-label'>{shortMonth(d[labelKey]) || d[labelKey]}</span>
          </div>
        )
      })}
    </div>
  )
}

// ── Stat card ─────────────────────────────────────────────────────
function StatCard({ label, value, sub, color, icon, onClick }) {
  return (
    <div className={`adash-stat ${onClick ? 'adash-stat--link' : ''}`} onClick={onClick}>
      <div className='adash-stat-icon' style={{ color }}>{icon}</div>
      <div className='adash-stat-body'>
        <span className='adash-stat-value' style={{ color }}>{value}</span>
        <span className='adash-stat-label'>{label}</span>
        {sub && <span className='adash-stat-sub'>{sub}</span>}
      </div>
    </div>
  )
}

// ── Add income modal ──────────────────────────────────────────────
function AddIncomeModal({ onClose, onAdded }) {
  const [form, setForm] = useState({
    label: '', amount: '', category: 'project',
    received_at: new Date().toISOString().split('T')[0], notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState(null)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.label || !form.amount) { setError('Label and amount are required.'); return }
    setSaving(true); setError(null)
    try {
      const res = await apiFetch('/api/admin/income', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, amount: parseFloat(form.amount) }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Failed.')
      onAdded()
    } catch(e) { setError(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div className='adash-overlay' onClick={onClose}>
      <div className='adash-modal' onClick={e => e.stopPropagation()}>
        <div className='adash-modal-header'>
          <h3>Log Income</h3>
          <button className='adash-modal-close' onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className='adash-modal-body'>
          <div className='adash-form-row'>
            <div className='adash-field'>
              <label>Label <span>*</span></label>
              <input value={form.label} onChange={e => set('label', e.target.value)} placeholder='e.g. Soko Fresh — Phase 1' />
            </div>
            <div className='adash-field'>
              <label>Amount (KSh) <span>*</span></label>
              <input type='number' value={form.amount} onChange={e => set('amount', e.target.value)} placeholder='150000' />
            </div>
          </div>
          <div className='adash-form-row'>
            <div className='adash-field'>
              <label>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}>
                {INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div className='adash-field'>
              <label>Date Received</label>
              <input type='date' value={form.received_at} onChange={e => set('received_at', e.target.value)} />
            </div>
          </div>
          <div className='adash-field'>
            <label>Notes <span className='opt'>(optional)</span></label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder='Any additional context…' />
          </div>
          {error && <p className='adash-error'> {error}</p>}
        </div>
        <div className='adash-modal-footer'>
          <button className='adash-btn-cancel' onClick={onClose}>Cancel</button>
          <button className='adash-btn-save' onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Log Income'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main dashboard ────────────────────────────────────────────────
export default function AdminDashboard() {
  const [data, setData]         = useState(null)
  const [income, setIncome]     = useState([])
  const [loading, setLoading]   = useState(true)
  const [showModal, setModal]   = useState(false)
  const navigate                = useNavigate()

  const load = async () => {
    try {
      const [dashRes, incRes] = await Promise.all([
        apiFetch('/api/admin/dashboard'),
        apiFetch('/api/admin/projects/income'),
      ])
      const [dash, inc] = await Promise.all([dashRes.json(), incRes.json()])
      setData(dash)
      setIncome(inc.entries || [])
    } catch { /* silent */ }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const deleteIncome = async (id) => {
    if (!window.confirm('Delete this income entry?')) return
    await apiFetch(`/api/admin/income/${id}`, { method: 'DELETE' })
    setIncome(prev => prev.filter(e => e.id !== id))
    load() // refresh dashboard totals
  }

  const atCapacity = data && data.active_projects >= data.max_projects

  return (
    <div className='adash-shell'>
      <AdminSidebar />

      <main className='adash-main'>
        {/* Topbar */}
        <div className='adash-topbar'>
          <div>
            <h1 className='adash-title'>Dashboard</h1>
            <p className='adash-sub'>Welcome back — here's what's happening</p>
          </div>
          <button className='adash-btn-income' onClick={() => setModal(true)}>
            + Log Income
          </button>
        </div>

        {loading && (
          <div className='adash-loading'>
            <div className='adash-spinner'></div>
            <p>Loading dashboard…</p>
          </div>
        )}

        {!loading && data && (
          <>
            {/* ── Capacity alert ── */}
            {atCapacity && (
              <div className='adash-alert'>
                <span></span>
                <p>You're at full capacity ({data.active_projects}/{data.max_projects} active projects). New quote submissions will see the capacity warning.</p>
                <button onClick={() => navigate('/admin/settings')}>Manage →</button>
              </div>
            )}

            {/* ── Pending testimonials alert ── */}
            {data.testimonials_pending > 0 && (
              <div className='adash-alert adash-alert--yellow'>
                <span>⏳</span>
                <p>{data.testimonials_pending} review{data.testimonials_pending > 1 ? 's' : ''} waiting for your approval.</p>
                <button onClick={() => navigate('/admin/testimonials')}>Review →</button>
              </div>
            )}

            {/* ── Top stat cards ── */}
            <div className='adash-stats-grid'>
              <StatCard
                label='Total Revenue'
                value={fmt(data.income_total)}
                sub={`${fmt(data.income_this_month)} this month`}
                color='#FFB7CE'
                icon='₭'
              />
              <StatCard
                label='Quote Requests'
                value={data.quotes_total}
                sub={`${data.quotes_new} new · ${data.quotes_in_progress} in progress`}
                color='#64b4ff'
                icon=''
                onClick={() => navigate('/admin/quotes')}
              />
              <StatCard
                label='Active Projects'
                value={`${data.active_projects} / ${data.max_projects}`}
                sub={atCapacity ? 'At capacity' : `${data.max_projects - data.active_projects} slot${data.max_projects - data.active_projects !== 1 ? 's' : ''} open`}
                color={atCapacity ? '#ff8080' : '#78dca0'}
                icon=''
                onClick={() => navigate('/admin/settings')}
              />
              <StatCard
                label='Avg Rating'
                value={data.avg_rating ? `${data.avg_rating} ` : 'No ratings'}
                sub={`${data.testimonials_approved} approved review${data.testimonials_approved !== 1 ? 's' : ''}`}
                color='#ffc850'
                icon=''
                onClick={() => navigate('/admin/testimonials')}
              />
              <StatCard
                label='Live Products'
                value={data.products_live}
                sub={`${data.products_total} total`}
                color='#b48aff'
                icon=''
                onClick={() => navigate('/admin/products')}
              />
              <StatCard
                label='Jobs Closed'
                value={data.quotes_closed}
                sub='Completed projects'
                color='#78dca0'
                icon=''
              />
            </div>

            {/* ── Charts row ── */}
            <div className='adash-charts-row'>
              <div className='adash-chart-card'>
                <div className='adash-chart-header'>
                  <h3>Quote Requests</h3>
                  <span>Last 6 months</span>
                </div>
                <BarChart
                  data={data.quotes_by_month}
                  valueKey='count'
                  labelKey='month'
                  color='#64b4ff'
                />
              </div>

              <div className='adash-chart-card'>
                <div className='adash-chart-header'>
                  <h3>Revenue</h3>
                  <span>Last 6 months</span>
                </div>
                <BarChart
                  data={data.income_by_month}
                  valueKey='total'
                  labelKey='month'
                  color='#FFB7CE'
                  prefix='KSh '
                />
              </div>
            </div>

            {/* ── Bottom row ── */}
            <div className='adash-bottom-row'>

              {/* Recent quotes */}
              <div className='adash-panel'>
                <div className='adash-panel-header'>
                  <h3>Recent Quotes</h3>
                  <button className='adash-panel-link' onClick={() => navigate('/admin/quotes')}>View all →</button>
                </div>
                {data.recent_quotes?.length === 0 && <p className='adash-empty-chart'>No quotes yet</p>}
                {data.recent_quotes?.map(q => (
                  <div key={q.id} className='adash-recent-row' onClick={() => navigate('/admin/quotes')}>
                    <div className='adash-recent-avatar'>
                      {q.name?.split(' ').map(w => w[0]).slice(0,2).join('').toUpperCase()}
                    </div>
                    <div className='adash-recent-info'>
                      <span className='adash-recent-name'>{q.name}</span>
                      <span className='adash-recent-meta'>{q.budget}</span>
                    </div>
                    <span className={`adash-recent-status adash-status--${q.status}`}>
                      {q.status.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>

              {/* Income log */}
              <div className='adash-panel'>
                <div className='adash-panel-header'>
                  <h3>Income Log</h3>
                  <button className='adash-panel-link' onClick={() => setModal(true)}>+ Add</button>
                </div>

                {/* Category breakdown */}
                {data.income_by_category?.length > 0 && (
                  <div className='adash-cat-breakdown'>
                    {data.income_by_category.map(c => (
                      <div key={c.category} className='adash-cat-row'>
                        <span className='adash-cat-dot' style={{ background: CAT_COLORS[c.category] || '#fff' }}></span>
                        <span className='adash-cat-label'>{c.category}</span>
                        <span className='adash-cat-val'>{fmt(c.total)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className='adash-income-list'>
                  {income.length === 0 && <p className='adash-empty-chart'>No income logged yet</p>}
                  {income.slice(0, 6).map(e => (
                    <div key={e.id} className='adash-income-row'>
                      <span className='adash-income-dot' style={{ background: CAT_COLORS[e.category] || '#fff' }}></span>
                      <div className='adash-income-info'>
                        <span className='adash-income-label'>{e.label}</span>
                        <span className='adash-income-date'>{e.received_at?.slice(0, 10)}</span>
                      </div>
                      <span className='adash-income-amount'>{fmt(e.amount)}</span>
                      <button className='adash-income-del' onClick={() => deleteIncome(e.id)} title='Delete'>×</button>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </>
        )}
      </main>

      {showModal && (
        <AddIncomeModal
          onClose={() => setModal(false)}
          onAdded={() => { setModal(false); load() }}
        />
      )}
    </div>
  )
}